import { z } from 'zod';
import { Address } from './common.js';
import type { TemplateConfig, TemplateDefaults } from './catalog.js';
import {
  TEMPLATE_LEDGER,
  type TemplateLedger,
  accountOverlayErrors,
  describeAccountOverlay,
  describeJournalOverlay,
  journalOverlayErrors,
  normalizeAccountOverlays,
  normalizeJournalOverlays,
} from './overlays.js';

/**
 * Intake questionnaire as data (spec 8.1). The wizard renders from this and the
 * backend validates with the same definitions.
 */

export const SECTIONS = [
  { id: 'venue', title: 'Venue and contract' },
  { id: 'entity', title: 'Legal entity identity' },
  { id: 'finance', title: 'Finance setup' },
  { id: 'ledger', title: 'Ledger overlays' },
  { id: 'procurement', title: 'Procurement and AP' },
  { id: 'people', title: 'People and approvals' },
  { id: 'workflows', title: 'Workflows' },
] as const;
export type SectionId = (typeof SECTIONS)[number]['id'];

export type Answers = Record<string, unknown>;

export interface QuestionContext {
  /** yyyy-mm-dd, the "today" used for date rules */
  today: string;
  goLiveLeadDays: number;
  template: TemplateConfig;
  defaults: TemplateDefaults;
  activeTemplates: Array<{ id: string; name: string }>;
  /** The template company's ledger, for the overlay checks (the environment's; USMF when absent) */
  ledger?: TemplateLedger;
}

export type QuestionType =
  | 'text'
  | 'date'
  | 'choice'
  | 'multi'
  | 'yesno'
  | 'address'
  | 'user'
  | 'vendors'
  | 'workflows'
  /** Overlay B rows (JournalOverlay[]) */
  | 'journals'
  /** Overlay C rows (AccountOverlay[]) */
  | 'accountOverrides';

export interface Option {
  value: string;
  label: string;
}

export interface Question {
  key: string;
  section: SectionId;
  label: string;
  help?: string;
  type: QuestionType;
  /** Static, or dependent on other answers. */
  required: boolean | ((a: Answers, ctx: QuestionContext) => boolean);
  visible?: (a: Answers, ctx: QuestionContext) => boolean;
  sensitive?: boolean;
  options?: (ctx: QuestionContext) => Option[];
  defaultValue?: (ctx: QuestionContext) => unknown;
  /** Returns error messages for a present value. */
  validate?: (value: unknown, a: Answers, ctx: QuestionContext) => string[];
}

// ── helpers ──────────────────────────────────────────────────────────────────

const opts =
  (...values: string[]): (() => Option[]) =>
  () =>
    values.map((v) => ({ value: v, label: v }));

const zodErrors = (schema: z.ZodTypeAny, value: unknown): string[] => {
  const r = schema.safeParse(value);
  return r.success ? [] : r.error.issues.map((i) => i.message);
};

const DATE = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date (yyyy-mm-dd)');

export function addDays(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export const US_TIME_ZONES: Option[] = [
  { value: 'America/New_York', label: 'Eastern (America/New_York)' },
  { value: 'America/Chicago', label: 'Central (America/Chicago)' },
  { value: 'America/Denver', label: 'Mountain (America/Denver)' },
  { value: 'America/Phoenix', label: 'Arizona (America/Phoenix)' },
  { value: 'America/Los_Angeles', label: 'Pacific (America/Los_Angeles)' },
  { value: 'America/Anchorage', label: 'Alaska (America/Anchorage)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (Pacific/Honolulu)' },
];

/** Finance answers whose change from the template default needs a reason. */
export const FINANCE_DEFAULT_KEYS = [
  'finance.currency',
  'finance.fiscalCalendar',
  'finance.chartOfAccounts',
] as const;

const FINANCE_DEFAULT_FIELDS: Record<(typeof FINANCE_DEFAULT_KEYS)[number], keyof TemplateDefaults> = {
  'finance.currency': 'currency',
  'finance.fiscalCalendar': 'fiscalCalendar',
  'finance.chartOfAccounts': 'chartOfAccounts',
};

/** True when any finance default was changed (a reason is then required). */
export function financeDeviates(a: Answers, ctx: QuestionContext): boolean {
  return FINANCE_DEFAULT_KEYS.some(
    (k) => a[k] !== undefined && a[k] !== ctx.defaults[FINANCE_DEFAULT_FIELDS[k]],
  );
}

/** True when the request adds any ledger overlay (a reason is then required). */
export function hasOverlays(a: Answers): boolean {
  return (
    normalizeJournalOverlays(a['ledger.journals']).length > 0 ||
    normalizeAccountOverlays(a['ledger.accountOverrides']).length > 0
  );
}

/**
 * Whether an answer equals the template default. Lists of values compare as sets; lists of rows
 * (the overlays) and objects compare by content.
 */
export function sameAnswer(def: unknown, value: unknown): boolean {
  if (Array.isArray(def) && Array.isArray(value)) {
    if (def.every((d) => typeof d !== 'object') && value.every((v) => typeof v !== 'object'))
      return def.length === value.length && def.every((d) => value.includes(d));
    return JSON.stringify(def) === JSON.stringify(value);
  }
  if (def && value && typeof def === 'object' && typeof value === 'object')
    return JSON.stringify(def) === JSON.stringify(value);
  return def === value;
}

/** One line per value, for the review screens and the request file. */
export function answerLines(q: Question, value: unknown, ctx?: QuestionContext): string[] {
  if (value === undefined || value === null || value === '') return [];
  if (q.type === 'journals') return normalizeJournalOverlays(value).map(describeJournalOverlay);
  if (q.type === 'accountOverrides')
    return normalizeAccountOverlays(value).map((r) => describeAccountOverlay(r, ctx?.ledger));
  if (typeof value === 'boolean') return [value ? 'Yes' : 'No'];
  const label = (v: unknown): string =>
    (ctx && q.options?.(ctx).find((o) => o.value === v)?.label) ?? String(v);
  if (Array.isArray(value)) return value.length ? [value.map(label).join(', ')] : [];
  if (typeof value === 'object') {
    const text = Object.values(value as Record<string, unknown>)
      .filter(Boolean)
      .join(', ');
    return text ? [text] : [];
  }
  return [label(value)];
}

/** What the template has for a question, in words (the "Template" column of a deviation). */
export function templateLines(q: Question, ctx: QuestionContext): string[] {
  if (q.type === 'journals')
    return [`Template journal names only: ${(ctx.ledger ?? TEMPLATE_LEDGER).journalNames.join(', ')}`];
  if (q.type === 'accountOverrides')
    return [
      `Template overrides only (accounts ${(ctx.ledger ?? TEMPLATE_LEDGER).overriddenAccounts.join(', ')}); every other account as in the shared chart`,
    ];
  return q.defaultValue ? answerLines(q, q.defaultValue(ctx), ctx) : [];
}

// ── questions ────────────────────────────────────────────────────────────────

export const QUESTIONS: Question[] = [
  // Venue and contract
  {
    key: 'venue.name',
    section: 'venue',
    label: 'Venue name',
    type: 'text',
    required: true,
    validate: (v) => zodErrors(z.string().max(60, 'At most 60 characters'), v),
  },
  { key: 'venue.contractRef', section: 'venue', label: 'Contract reference', type: 'text', required: true },
  {
    key: 'venue.contractSignedDate',
    section: 'venue',
    label: 'Contract signed date',
    type: 'date',
    required: true,
    // Demo: any date is accepted (spec 8.1 "not in future" relaxed).
    validate: (v) => zodErrors(DATE, v),
  },
  {
    key: 'venue.targetGoLive',
    section: 'venue',
    label: 'Target go-live date',
    type: 'date',
    required: true,
    // Demo: no lead-time rule (spec 8.1 GO_LIVE_LEAD_DAYS relaxed).
    validate: (v) => zodErrors(DATE, v),
  },
  {
    key: 'venue.type',
    section: 'venue',
    label: 'Venue type',
    type: 'choice',
    required: true,
    options: opts('Stadium', 'Arena', 'Convention center', 'Other'),
  },
  {
    key: 'venue.modules',
    section: 'venue',
    label: 'Modules in use',
    type: 'multi',
    required: true,
    options: opts('Procurement', 'AP', 'Projects'),
    validate: (v) => (Array.isArray(v) && v.length > 0 ? [] : ['Select at least one module']),
  },

  // Legal entity identity
  {
    key: 'entity.id',
    section: 'entity',
    label: 'Legal entity ID',
    help: 'Company ID in D365 (dataAreaId). Checked live for availability.',
    type: 'text',
    required: true,
    // Demo: the only rule besides the live availability check is D365's own
    // 4-character limit on company IDs (the template ID pattern is not enforced).
    validate: (v) => (String(v).length > 4 ? ['D365 company IDs are at most 4 characters'] : []),
  },
  { key: 'entity.legalName', section: 'entity', label: 'Legal name', type: 'text', required: true },
  {
    key: 'entity.searchName',
    section: 'entity',
    label: 'Search name',
    type: 'text',
    required: true,
    validate: (v) => zodErrors(z.string().max(20, 'At most 20 characters'), v),
  },
  {
    key: 'entity.address',
    section: 'entity',
    label: 'Primary address',
    type: 'address',
    required: true,
    validate: (v) => {
      // Name the missing parts ("Missing street and state") instead of repeating "Required".
      const a = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>;
      const parts = [
        ['street', 'street'],
        ['city', 'city'],
        ['state', 'state'],
        ['zip', 'ZIP'],
      ] as const;
      const missing = parts
        .filter(([k]) => typeof a[k] !== 'string' || !(a[k] as string).trim())
        .map(([, l]) => l);
      if (missing.length) {
        const list =
          missing.length === 1 ? missing[0] : `${missing.slice(0, -1).join(', ')} and ${missing.at(-1)}`;
        return [`Missing ${list}`];
      }
      return zodErrors(Address, v);
    },
  },
  {
    key: 'entity.timeZone',
    section: 'entity',
    label: 'Time zone',
    type: 'choice',
    required: true,
    options: () => US_TIME_ZONES,
  },
  {
    key: 'entity.ein',
    section: 'entity',
    label: 'EIN',
    help: 'Stored masked; only the last four digits are ever shown.',
    type: 'text',
    sensitive: true,
    required: true,
    validate: (v) => (/^\d{2}-\d{7}$/.test(String(v)) || isMaskedEin(v) ? [] : ['Format 12-3456789']),
  },

  // Finance setup
  {
    key: 'finance.templateId',
    section: 'finance',
    label: 'Template',
    type: 'choice',
    required: true,
    options: (ctx) => ctx.activeTemplates.map((t) => ({ value: t.id, label: t.name })),
    defaultValue: (ctx) => ctx.template.id,
  },
  {
    key: 'finance.currency',
    section: 'finance',
    label: 'Accounting currency',
    type: 'choice',
    required: true,
    options: opts('USD', 'CAD', 'EUR', 'GBP', 'MXN'),
    defaultValue: (ctx) => ctx.defaults.currency,
  },
  {
    key: 'finance.fiscalCalendar',
    section: 'finance',
    label: 'Fiscal calendar',
    type: 'text',
    required: true,
    defaultValue: (ctx) => ctx.defaults.fiscalCalendar,
  },
  {
    key: 'finance.chartOfAccounts',
    section: 'finance',
    label: 'Chart of accounts',
    type: 'text',
    required: true,
    defaultValue: (ctx) => ctx.defaults.chartOfAccounts,
  },
  {
    key: 'finance.deviationReason',
    section: 'finance',
    label: 'Reason for changing template defaults',
    type: 'text',
    required: (a, ctx) => financeDeviates(a, ctx),
    visible: (a, ctx) => financeDeviates(a, ctx),
  },
  {
    key: 'finance.venueDimensionValue',
    section: 'finance',
    label: 'Venue financial dimension value',
    help: 'Unique value of the Venue dimension for this company.',
    type: 'text',
    required: true,
  },
  {
    key: 'finance.copyNumberSequences',
    section: 'finance',
    label: 'Copy number sequences from the template',
    type: 'yesno',
    required: true,
    defaultValue: (ctx) => ctx.defaults.copyNumberSequences,
  },
  {
    key: 'finance.resetToSmallest',
    section: 'finance',
    label: 'Reset copied number sequences to their smallest value',
    type: 'yesno',
    required: true,
    visible: (a) => a['finance.copyNumberSequences'] === true,
    defaultValue: (ctx) => ctx.defaults.resetToSmallest,
  },
  {
    key: 'finance.openingBalances',
    section: 'finance',
    label: 'Opening balances needed',
    help: 'Out of scope for automation; creates a manual task.',
    type: 'yesno',
    required: true,
    defaultValue: () => false,
  },
  {
    key: 'finance.bankAccounts',
    section: 'finance',
    label: 'Bank accounts needed',
    help: 'Out of scope for automation; creates a manual task.',
    type: 'yesno',
    required: true,
    defaultValue: () => false,
  },
  {
    key: 'finance.intercompany',
    section: 'finance',
    label: 'Intercompany setup needed',
    help: 'Out of scope for automation; creates a manual task.',
    type: 'yesno',
    required: true,
    defaultValue: () => false,
  },

  // Ledger overlays: venue-only changes on top of the template (overlays.ts). Empty = the
  // template as is; anything added is a deviation the finance approver sees at gate 2.
  {
    key: 'ledger.journals',
    section: 'ledger',
    label: 'New journal names (journal control)',
    help:
      'Journal names this venue needs that the template does not have. Each one gets journal control: ' +
      'it can only post to the main account range you give. Template journals stay as they are.',
    type: 'journals',
    required: false,
    defaultValue: () => [],
    validate: (v, _a, ctx) => journalOverlayErrors(v, ctx.ledger),
  },
  {
    key: 'ledger.accountOverrides',
    section: 'ledger',
    label: 'Main account overrides for this venue',
    help:
      'Suspend main accounts or set a default financial dimension on them for this legal entity only. ' +
      'The chart of accounts is shared: the template company and other venues keep the accounts as they are.',
    type: 'accountOverrides',
    required: false,
    defaultValue: () => [],
    validate: (v, _a, ctx) => accountOverlayErrors(v, ctx.ledger),
  },
  {
    key: 'ledger.overlayReason',
    section: 'ledger',
    label: 'Reason for the overlays',
    help: 'Shown to the finance approver next to the overlays.',
    type: 'text',
    required: (a) => hasOverlays(a),
    visible: (a) => hasOverlays(a),
    validate: (v) => zodErrors(z.string().max(250, 'At most 250 characters'), v),
  },

  // Procurement and AP
  {
    key: 'procurement.vendorCopyMode',
    section: 'procurement',
    label: 'Vendors to copy from the template',
    type: 'choice',
    required: true,
    options: opts('All', 'Selected', 'None'),
  },
  {
    key: 'procurement.vendorIds',
    section: 'procurement',
    label: 'Vendors',
    type: 'vendors',
    required: (a) => a['procurement.vendorCopyMode'] === 'Selected',
    visible: (a) => a['procurement.vendorCopyMode'] === 'Selected',
    validate: (v) => (Array.isArray(v) && v.length > 0 ? [] : ['Select at least one vendor']),
  },
  {
    key: 'procurement.matchingPolicy',
    section: 'procurement',
    label: 'Invoice matching policy',
    type: 'choice',
    required: true,
    options: () => [
      { value: 'TwoWay', label: 'Two-way matching' },
      { value: 'ThreeWay', label: 'Three-way matching' },
    ],
    defaultValue: (ctx) => ctx.defaults.matchingPolicy,
  },

  // Projects step (projects.enabled / managerUpn / controllerUpn, spec 8.1) and
  // people rule P9 are deferred: removed from the build until the project roles
  // are settled. The Projects module still copies project setup from the template.

  // Workflows
  {
    key: 'workflows.selected',
    section: 'workflows',
    label: 'Approval workflows to import',
    type: 'workflows',
    required: true,
    options: (ctx) => ctx.template.workflowLibrary.map((w) => ({ value: w.key, label: w.name })),
    defaultValue: (ctx) => ctx.template.workflowLibrary.map((w) => w.key),
    validate: (v) => (Array.isArray(v) && v.length > 0 ? [] : ['Select at least one workflow']),
  },
];

export const QUESTION_BY_KEY: ReadonlyMap<string, Question> = new Map(QUESTIONS.map((q) => [q.key, q]));

export function isMaskedEin(v: unknown): boolean {
  return typeof v === 'string' && /^\*\*-\*\*\*\d{4}$/.test(v);
}

export function maskEin(ein: string): string {
  return `**-***${ein.replace(/\D/g, '').slice(-4)}`;
}
